import React, { useState, useEffect } from "react";
import { Text, View } from "react-native";
import { EStyleSheet, useDynamicStyles } from "react-native-extended-stylesheet-typescript";
import { GlobalStyles } from "../style/Style";
import { HorseRacingPrompt, HorseRaceWagerType, SubmitHorseRacePicks } from "@freeplay/shared-code";
import { GameActions } from "../../controllers/GameController";
import { PropSelectionButton } from "./PropSelectionButton";

export interface SimpleHorseRacingPropProps {
    propType: HorseRaceWagerType;
    propValue: HorseRacingPrompt;
    currentPick: number;
    makeHorseRacePick: React.Dispatch<GameActions>;
    current: SubmitHorseRacePicks;
    reviewPicksState?: boolean;
}

export const SimpleHorseRacingProp = ({
    current,
    propType,
    propValue,
    makeHorseRacePick,
    currentPick,
    reviewPicksState,
}: SimpleHorseRacingPropProps): JSX.Element => {
    // eslint-disable-next-line
    const styles = useDynamicStyles(generateStyles);
    const [isSelected, setIsSelected] = useState<number>();

    let buttons: JSX.Element;

    const handleSelection = (val: number) => {
        // horse to win or top 4: yes(0) and no(1)
        //winning distance and time : over(1) exactly(0) under(-1)
        makeHorseRacePick({
            type: "setHorseSimpleSelection",
            selection: val,
            promptIndex: currentPick,
        });
    };
    useEffect(() => {
        setIsSelected(current?.choices[0]);
    }, [current?.choices]);

    if (propType === HorseRaceWagerType.HORSETOWIN || propType === HorseRaceWagerType.TOP4) {
        buttons = (
            <View style={styles.buttonContainer}>
                <PropSelectionButton
                    reviewPicksState={reviewPicksState}
                    buttonText={"Yes"}
                    disabled={isSelected === 1}
                    width={100}
                    margin={{ right: 5 }}
                    isActive={isSelected === 1}
                    onPress={() => handleSelection(1)}
                />
                <PropSelectionButton
                    reviewPicksState={reviewPicksState}
                    buttonText={"No"}
                    disabled={isSelected === 0}
                    width={100}
                    margin={{ left: 5 }}
                    isActive={isSelected === 0}
                    onPress={() => handleSelection(0)}
                />
            </View>
        );
    } else {
        buttons = (
            <View style={styles.buttonContainer}>
                <PropSelectionButton
                    reviewPicksState={reviewPicksState}
                    buttonText={"Over"}
                    disabled={isSelected === 1}
                    width={80}
                    margin={{ right: 5 }}
                    isActive={isSelected === 1}
                    onPress={() => handleSelection(1)}
                />
                <PropSelectionButton
                    reviewPicksState={reviewPicksState}
                    buttonText={"Under"}
                    disabled={isSelected === -1}
                    width={80}
                    margin={{ right: 5, left: 5 }}
                    isActive={isSelected === -1}
                    onPress={() => handleSelection(-1)}
                />
                <PropSelectionButton
                    reviewPicksState={reviewPicksState}
                    buttonText={"Exactly"}
                    disabled={isSelected === 0}
                    width={80}
                    margin={{ left: 5 }}
                    isActive={isSelected === 0}
                    onPress={() => handleSelection(0)}
                />
            </View>
        );
    }

    const getPropString = () => {
        switch (propType) {
            case HorseRaceWagerType.HORSETOWIN: {
                return (
                    <Text style={styles.propString}>
                        Will{" "}
                        <Text style={styles.propValue}>
                            {
                                propValue.horseRaceEvent.horseData.find(
                                    (d) => d.programNumber === propValue.horseProgramNumber,
                                )?.horseName
                            }
                        </Text>{" "}
                        win?
                    </Text>
                );
            }
            case HorseRaceWagerType.TOP4: {
                return (
                    <Text style={styles.propString}>
                        Will{" "}
                        <Text style={styles.propValue}>
                            {
                                propValue.horseRaceEvent.horseData.find(
                                    (d) => d.programNumber === propValue.horseProgramNumber,
                                )?.horseName
                            }
                        </Text>{" "}
                        finish in the top 4?
                    </Text>
                );
            }
            case HorseRaceWagerType.WINNINGTIME: {
                return (
                    <Text style={styles.propString}>
                        Winning time: <Text style={styles.propValue}>{propValue.winningTime}</Text>
                    </Text>
                );
            }
            case HorseRaceWagerType.WINNINGDISTANCE: {
                return (
                    <Text style={styles.propString}>
                        Winning distance: <Text style={styles.propValue}>{propValue.winningDistance}</Text>
                    </Text>
                );
            }
        }
    };

    return (
        <View style={[styles.prop]}>
            {getPropString()}
            {buttons}
        </View>
    );
};

const generateStyles = () => {
    return EStyleSheet.create({
        prop: {
            width: "100%",
            justifyContent: "center",
            alignItems: "center",
        },
        propString: {
            color: () => EStyleSheet.value(GlobalStyles.BASE_COLOUR),
            fontWeight: "700",
            fontSize: 12,
            marginTop: 10,
        },
        propValue: {
            color: () => EStyleSheet.value(GlobalStyles.ACCENT_COLOUR),
        },
        buttonContainer: {
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            marginTop: 5,
        },
    });
};
